import { OrgRoles, Roles } from "@aura-frontend/omnibus-az-client";

import { AppRoute, pathStartsWith, ROUTES } from "./routes";

const adminRoutes = Object.values(ROUTES).filter(
  (route) => !route.includes(ROUTES.DASHBOARD),
);

const nonSuperAdminRoutes = adminRoutes;

// Define which routes each role can access
export const ROLE_PERMISSIONS: Partial<Record<Roles | OrgRoles, AppRoute[]>> = {
  // System Admins - Full access
  magnus_super_admin: adminRoutes,
  magnus_admin: nonSuperAdminRoutes,
  aura_super_admin: nonSuperAdminRoutes,
  aura_admin: nonSuperAdminRoutes,

  // Staff roles - Access to everything except accounts, verification, and profiles
  staff_admin: [
    ROUTES.ADMIN_TOOLS_ACCOUNT_VERIFICATION,
    ROUTES.BASE,
    ROUTES.CHALLENGE_MODE,
    ROUTES.CLAIM_PERMISSIONS,
    ROUTES.LIVE_TRACKING,
    ROUTES.DASHBOARD,
    ROUTES.ADMIN_TOOLS_FIND_CSV,
    ROUTES.EVENT_LOG,
    ROUTES.EVENT_SCHEDULE,
    ROUTES.MANAGE_SMARTPARKS,
    ROUTES.MANAGE,
    ROUTES.MANAGE_OPERATORS,
    ROUTES.MANAGE_ROSTERS,
    ROUTES.MANAGE_BILLING,
    ROUTES.PLAYER_BY_ID,
    ROUTES.REPORTS,
    ROUTES.MANAGE_PLAYERS,
    ROUTES.SETTINGS,
    ROUTES.STATS,
    ROUTES.TAGGING,
    ROUTES.TAGGING_V2,
  ],
  staff: [
    ROUTES.ADMIN_TOOLS_ACCOUNT_VERIFICATION,
    ROUTES.BASE,
    ROUTES.CHALLENGE_MODE,
    ROUTES.CLAIM_PERMISSIONS,
    ROUTES.LIVE_TRACKING,
    ROUTES.DASHBOARD,
    ROUTES.ADMIN_TOOLS_FIND_CSV,
    ROUTES.EVENT_LOG,
    ROUTES.EVENT_SCHEDULE,
    ROUTES.MANAGE_SMARTPARKS,
    ROUTES.MANAGE,
    ROUTES.MANAGE_OPERATORS,
    ROUTES.MANAGE_ROSTERS,
    ROUTES.MANAGE_BILLING,
    ROUTES.PLAYER_BY_ID,
    ROUTES.REPORTS,
    ROUTES.MANAGE_PLAYERS,
    ROUTES.SETTINGS,
    ROUTES.STATS,
    ROUTES.TAGGING,
    ROUTES.TAGGING_V2,
  ],

  // Account owners - Same access as staff
  account_owner: [
    ROUTES.ADMIN_TOOLS_ACCOUNT_VERIFICATION,
    ROUTES.BASE,
    ROUTES.CHALLENGE_MODE,
    ROUTES.CLAIM_PERMISSIONS,
    ROUTES.LIVE_TRACKING,
    ROUTES.DASHBOARD,
    ROUTES.ADMIN_TOOLS_FIND_CSV,
    ROUTES.EVENT_LOG,
    ROUTES.EVENT_SCHEDULE,
    ROUTES.MANAGE_SMARTPARKS,
    ROUTES.MANAGE,
    ROUTES.MANAGE_OPERATORS,
    ROUTES.MANAGE_ROSTERS,
    ROUTES.MANAGE_BILLING,
    ROUTES.REPORTS,
    ROUTES.MANAGE_PLAYERS,
    ROUTES.SETTINGS,
    ROUTES.STATS,
    ROUTES.TAGGING,
    ROUTES.TAGGING_V2,
  ],

  // Taggers - Only tagging
  tagger: [
    ROUTES.BASE,
    ROUTES.CLAIM_PERMISSIONS,
    ROUTES.TAGGING,
    ROUTES.TAGGING_V2,
  ],

  // Other roles - Only dashboard
  viewer: [ROUTES.BASE, ROUTES.CLAIM_PERMISSIONS, ROUTES.DASHBOARD],
  unverified_account_owner: [
    ROUTES.BASE,
    ROUTES.CLAIM_PERMISSIONS,
    ROUTES.DASHBOARD,
  ],
};

// Check if a user has permission for a specific path
export const hasPermissionForPath = (
  userRoles: (Roles | OrgRoles)[],
  path: string,
): boolean => {
  // If user has no roles, they can only access the no-access page and claim permissions
  if (userRoles.length === 0) {
    return (
      pathStartsWith(path, ROUTES.NO_ACCESS) ||
      pathStartsWith(path, ROUTES.BASE) ||
      pathStartsWith(path, ROUTES.CLAIM_PERMISSIONS)
    );
  }

  // Check if user has any role that allows access to this path
  return userRoles.some((role) => {
    const allowedPaths = ROLE_PERMISSIONS[role];
    if (!allowedPaths) return false;

    // Admin roles have access to everything
    if (allowedPaths.includes("*" as AppRoute)) return true;

    // Check if the current path starts with any of the allowed paths
    return allowedPaths.some((allowedPath) =>
      pathStartsWith(path, allowedPath),
    );
  });
};

export const isAdminUser = (roles: (Roles | OrgRoles)[]): boolean => {
  return roles.some(
    (role) =>
      role === "magnus_super_admin" ||
      role === "magnus_admin" ||
      role === "aura_super_admin" ||
      role === "aura_admin" ||
      role === "account_owner",
  );
};

export const isMagnusAdminUser = (roles: (Roles | OrgRoles)[]): boolean => {
  return roles.some(
    (role) => role === "magnus_super_admin" || role === "magnus_admin",
  );
};

export const isTaggerUser = (roles: (Roles | OrgRoles)[]): boolean => {
  return roles.length === 1 && roles[0] === "tagger";
};

/**
 * Get the home route for a user based on their roles
 * @param roles - Combined array of user roles and org roles
 * @returns The appropriate home route for the user
 */
export const getHomeRoute = (roles: (Roles | OrgRoles)[]): AppRoute => {
  // Magnus admins go to the smartparks admin page
  if (roles.includes("magnus_super_admin") || roles.includes("magnus_admin")) {
    return ROUTES.MANAGE_SMARTPARKS_ADMIN;
  }

  // Taggers only have access to tagging
  if (isTaggerUser(roles)) {
    return ROUTES.TAGGING;
  }

  // Users with no roles need to claim access first
  if (roles.length === 0) {
    return ROUTES.NO_ACCESS;
  }

  return ROUTES.MANAGE_SMARTPARKS;
};
